import React, { useState, useEffect } from 'react';
import { productsAPI, API_BASE_URL } from '../../api';
import EditProductModal from '../product/EditProductModal';
import DuplicateProductModal from '../product/DuplicateProductModal';
import '../../styles/ProductManagement.css';

function ProductManagement() {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [loading, setLoading] = useState(true);

    const [product, setProduct] = useState({
        name: '',
        price: '',
        category: 'aneis',
        total_installments: 10,
        max_installments_no_interest: 0,
        stock: 0,
        description: {
            comprimento: '',
            material: '',
            pedras: '',
            sugestao: '',
            observacao: ''
        },
        images: []
    });

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        filterAndSortProducts();
    }, [products, searchTerm, categoryFilter, sortBy]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const data = await productsAPI.getAll();
            setProducts(data);
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortProducts = () => {
        let filtered = [...products];

        // Filtrar por busca
        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtrar por categoria
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(p => p.category === categoryFilter);
        }

        // Ordenar
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'price-asc':
                    return parseFloat(a.price) - parseFloat(b.price);
                case 'price-desc':
                    return parseFloat(b.price) - parseFloat(a.price);
                case 'stock':
                    return (b.stock || 0) - (a.stock || 0);
                default:
                    return 0;
            }
        });

        setFilteredProducts(filtered);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProduct(prevProduct => ({
            ...prevProduct,
            [name]: ['price', 'max_installments_no_interest', 'stock'].includes(name)
                ? parseFloat(value) || 0
                : value
        }));
    };

    const handleDescriptionChange = (e) => {
        const { name, value } = e.target;
        setProduct(prevProduct => ({
            ...prevProduct,
            description: {
                ...prevProduct.description,
                [name]: value
            }
        }));
    };

    const handleImageChange = (e) => {
        setProduct(prevProduct => ({
            ...prevProduct,
            images: [...e.target.files]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', product.name);
        formData.append('price', product.price);
        formData.append('category', product.category);
        formData.append('total_installments', product.total_installments);
        formData.append('max_installments_no_interest', product.max_installments_no_interest);
        formData.append('stock', product.stock);
        formData.append('description', JSON.stringify(product.description));

        for (let i = 0; i < product.images.length; i++) {
            formData.append('images', product.images[i]);
        }

        try {
            const newProduct = await productsAPI.create(formData);
            setProducts([...products, newProduct]);
            setProduct({
                name: '',
                price: '',
                category: 'aneis',
                total_installments: 10,
                max_installments_no_interest: 0,
                stock: 0,
                description: {
                    comprimento: '',
                    material: '',
                    pedras: '',
                    sugestao: '',
                    observacao: ''
                },
                images: []
            });
            setShowAddForm(false);
            alert('Produto adicionado com sucesso!');
        } catch (error) {
            console.error("Erro ao adicionar o produto:", error);
            alert('Erro ao adicionar produto');
        }
    };

    const handleEditClick = (product) => {
        setSelectedProduct(product);
        setIsEditModalOpen(true);
    };

    const handleDuplicateClick = (product) => {
        setSelectedProduct(product);
        setIsDuplicateModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setSelectedProduct(null);
    };

    const handleCloseDuplicateModal = () => {
        setIsDuplicateModalOpen(false);
        setSelectedProduct(null);
    };

    const handleSaveProduct = async (updatedProduct) => {
        try {
            await productsAPI.update(updatedProduct.id, updatedProduct);
            setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
            handleCloseModal();
            alert('Produto atualizado com sucesso!');
        } catch (error) {
            console.error("Erro ao salvar o produto:", error);
            alert('Erro ao atualizar produto');
        }
    };

    const handleSaveDuplicateProduct = async (duplicateProduct) => {
        try {
            const newProduct = await productsAPI.create(duplicateProduct);
            setProducts([...products, newProduct]);
            handleCloseDuplicateModal();
            alert('Produto duplicado com sucesso!');
        } catch (error) {
            console.error("Erro ao duplicar o produto:", error);
            alert('Erro ao duplicar produto');
        }
    };

    const handleDelete = async (productId) => {
        if (window.confirm("Tem certeza que deseja excluir este produto?")) {
            try {
                await productsAPI.delete(productId);
                setProducts(products.filter(p => p.id !== productId));
                alert('Produto excluído com sucesso!');
            } catch (error) {
                console.error("Erro ao excluir o produto:", error);
                alert('Erro ao excluir produto');
            }
        }
    };

    const categories = ['aneis', 'brincos', 'colares', 'pulseiras', 'conjuntos'];

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Carregando produtos...</p>
            </div>
        );
    }

    return (
        <div className="product-management">
            {/* Header */}
            <div className="management-header">
                <h2>📦 Gerenciar Produtos</h2>
                <button
                    className="btn-add-product"
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    {showAddForm ? '✕ Cancelar' : '+ Adicionar Produto'}
                </button>
            </div>

            {/* Formulário de Adicionar (Colapsável) */}
            {showAddForm && (
                <div className="add-product-form">
                    <h3>➕ Novo Produto</h3>
                    <form onSubmit={handleSubmit} className="product-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="name">Nome do Produto *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={product.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="category">Categoria *</label>
                                <select
                                    id="category"
                                    name="category"
                                    value={product.category}
                                    onChange={handleChange}
                                    required
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>
                                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="price">Preço (R$) *</label>
                                <input
                                    type="number"
                                    id="price"
                                    name="price"
                                    step="0.01"
                                    value={product.price}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="stock">Estoque *</label>
                                <input
                                    type="number"
                                    id="stock"
                                    name="stock"
                                    value={product.stock}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="max_installments_no_interest">Parcelas Sem Juros (máx. 10)</label>
                                <input
                                    type="number"
                                    id="max_installments_no_interest"
                                    name="max_installments_no_interest"
                                    value={product.max_installments_no_interest}
                                    onChange={handleChange}
                                    min="0"
                                    max="10"
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <h4>📝 Descrição</h4>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="material">Material</label>
                                    <input
                                        type="text"
                                        id="material"
                                        name="material"
                                        value={product.description.material}
                                        onChange={handleDescriptionChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="comprimento">Comprimento</label>
                                    <input
                                        type="text"
                                        id="comprimento"
                                        name="comprimento"
                                        value={product.description.comprimento}
                                        onChange={handleDescriptionChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="pedras">Pedras</label>
                                    <input
                                        type="text"
                                        id="pedras"
                                        name="pedras"
                                        value={product.description.pedras}
                                        onChange={handleDescriptionChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label htmlFor="sugestao">Sugestão</label>
                                    <input
                                        type="text"
                                        id="sugestao"
                                        name="sugestao"
                                        value={product.description.sugestao}
                                        onChange={handleDescriptionChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label htmlFor="observacao">Observação</label>
                                    <textarea
                                        id="observacao"
                                        name="observacao"
                                        value={product.description.observacao}
                                        onChange={handleDescriptionChange}
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label htmlFor="images">📷 Imagens (4 arquivos) *</label>
                            <input
                                type="file"
                                id="images"
                                name="images"
                                onChange={handleImageChange}
                                multiple
                                accept="image/*"
                                required
                            />
                            <small>Selecione até 4 imagens do produto</small>
                        </div>

                        <button type="submit" className="submit-button">
                            ✅ Adicionar Produto
                        </button>
                    </form>
                </div>
            )}

            {/* Barra de Filtros e Busca */}
            <div className="filters-section">
                <div className="search-box">
                    <i className="search-icon">🔍</i>
                    <input
                        type="text"
                        placeholder="Buscar por nome do produto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchTerm('')}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="filters-group">
                    <div className="filter-item">
                        <label>📂 Categoria:</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">Todas</option>
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-item">
                        <label>🔄 Ordenar por:</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="filter-select"
                        >
                            <option value="name">Nome (A-Z)</option>
                            <option value="price-asc">Preço (Menor)</option>
                            <option value="price-desc">Preço (Maior)</option>
                            <option value="stock">Estoque</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Informações */}
            <div className="products-info">
                <span className="total-products">
                    {filteredProducts.length} produto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
                </span>
                {searchTerm && (
                    <span className="search-info">
                        Buscando por: "<strong>{searchTerm}</strong>"
                    </span>
                )}
            </div>

            {/* Lista de Produtos */}
            {filteredProducts.length === 0 ? (
                <div className="no-products">
                    <div className="no-products-icon">📦</div>
                    <h3>Nenhum produto encontrado</h3>
                    <p>Tente ajustar os filtros ou adicionar novos produtos.</p>
                </div>
            ) : (
                <div className="products-grid">
                    {filteredProducts.map(p => {
                        return (
                            <div key={p.id} className="product-card">
                                <div className="product-image-container">
                                    <img
                                        src={(() => {
                                            const baseUrl = API_BASE_URL.replace('/api', '');
                                            if (p.image) {
                                                if (p.image.startsWith('http')) {
                                                    return p.image;
                                                }
                                                if (p.category) {
                                                    return `${baseUrl}/products/${p.category}/${p.image}`;
                                                }
                                                return `${baseUrl}/products/${p.image}`;
                                            }
                                            if (Array.isArray(p.images) && p.images.length > 0) {
                                                const firstImage = p.images[0];
                                                if (firstImage.startsWith('http')) {
                                                    return firstImage;
                                                }
                                                if (p.category) {
                                                    return `${baseUrl}/products/${p.category}/${firstImage}`;
                                                }
                                                return `${baseUrl}/products/${firstImage}`;
                                            }
                                            return 'https://placehold.co/250x250/FFD700/000000?text=Sem+Imagem';
                                        })()}
                                        alt={p.name}
                                        className="product-image"
                                        onError={(e) => {
                                            console.error('Erro ao carregar imagem. Tentando placeholder...');
                                            if (e.target.src !== 'https://placehold.co/250x250/FFD700/000000?text=Sem+Imagem') {
                                                e.target.src = 'https://placehold.co/250x250/FFD700/000000?text=Sem+Imagem';
                                            }
                                        }}
                                    />
                                    <div className="product-overlay">
                                        <button
                                            className="btn-edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditClick(p);
                                            }}
                                            title="Editar produto"
                                        >
                                            ✏️ Editar
                                        </button>
                                    </div>
                                </div>

                                <div className="product-info">
                                    <div className="product-category-badge">
                                        {p.category}
                                    </div>
                                    <h3 className="product-name">{p.name}</h3>

                                    <div className="product-details">
                                        <div className="product-price">
                                            R$ {parseFloat(p.price).toFixed(2)}
                                        </div>
                                        <div className="product-stock">
                                            <span className={`stock-badge ${(p.stock || 0) > 10 ? 'in-stock' : (p.stock || 0) > 0 ? 'low-stock' : 'out-stock'}`}>
                                                {(p.stock || 0) > 0 ? `${p.stock} em estoque` : 'Sem estoque'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="product-actions">
                                        <button
                                            className="btn-duplicate"
                                            onClick={() => handleDuplicateClick(p)}
                                        >
                                            &#128441; Duplicar
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => handleDelete(p.id)}
                                        >
                                            🗑️ Excluir
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Modal de Edição */}
            {isEditModalOpen && (
                <EditProductModal
                    product={selectedProduct}
                    onClose={handleCloseModal}
                    onSave={handleSaveProduct}
                />
            )}

            {/* Modal de Duplicação */}
            {isDuplicateModalOpen && (
                <DuplicateProductModal
                    product={selectedProduct}
                    onClose={handleCloseDuplicateModal}
                    onSave={handleSaveDuplicateProduct}
                />
            )}
        </div>
    );
}

export default ProductManagement;